import React from "react";
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  styled,
  TextField,
  Typography,
  Alert,
  Paper,
  Divider,
  Chip,
  Switch,
  FormControlLabel,
  MenuItem,
  Stack,
} from "@mui/material";
import { BEAUTY_PAGEANT_LABEL } from "../../../utils/homeFlow";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import StadiumOutlinedIcon from "@mui/icons-material/StadiumOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BannerImage from "../professionalTournaments/BannerImage";
import {
  ADVERTISEMENT_BANNER_WIDTH,
  ADVERTISEMENT_BANNER_HEIGHT,
} from "../../../utils/advertisementBannerSpec";
import PromoVideoUpload from "../competitions/PromoVideoUpload";
import dayjs from "dayjs";

const MAX_CATEGORIES = 10;
const MAX_EVENTS = 10;

const localNowMin = () => dayjs().format("YYYY-MM-DDTHH:mm");

const Field = styled(TextField)(() => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "10px",
    minHeight: 48,
    alignItems: "center",
    "& fieldset": { borderColor: "#e5e7eb" },
    "&:hover fieldset": { borderColor: "#c9ccd6" },
    "&.Mui-focused fieldset": { borderColor: "#ef364e" },
    "&.Mui-disabled": {
      backgroundColor: "#f3f4f6",
    },
  },
  "& .MuiOutlinedInput-input": {
    paddingTop: "12px",
    paddingBottom: "12px",
    display: "flex",
    alignItems: "center",
    color: "#111827",
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "#374151",
    color: "#374151",
  },
  "& .MuiInputAdornment-root": {
    height: "auto",
    maxHeight: "none",
    alignItems: "center",
  },
  "& input[type='datetime-local']": {
    lineHeight: "1.4",
    cursor: "pointer",
  },
  "& input[type='datetime-local']::-webkit-calendar-picker-indicator": {
    cursor: "pointer",
    opacity: 0.75,
    padding: 0,
    margin: 0,
    height: 20,
    width: 20,
    position: "absolute",
    right: 12,
    top: "50%",
    transform: "translateY(-50%)",
  },
  "& .MuiOutlinedInput-root.datetime-field": {
    cursor: "pointer",
    position: "relative",
  },
}));

const openDatePicker = (e) => {
  const input =
    e.currentTarget.querySelector?.("input[type='datetime-local']") ||
    (e.currentTarget.tagName === "INPUT" ? e.currentTarget : null);
  if (!input) return;
  try {
    if (typeof input.showPicker === "function") input.showPicker();
  } catch (_) {
    input.focus();
  }
};

const StageBlock = ({
  step,
  totalSteps = 2,
  eyebrow,
  title,
  subtitle,
  accent = "#ef364e",
  icon: Icon,
  children,
}) => (
  <Paper
    elevation={0}
    sx={{
      mb: 3.5,
      borderRadius: 3,
      overflow: "hidden",
      border: `1px solid ${accent}33`,
      background: "#fff",
      boxShadow: `0 10px 28px ${accent}12`,
    }}
  >
    <Box
      sx={{
        position: "relative",
        px: { xs: 2, md: 2.75 },
        py: { xs: 2, md: 2.4 },
        background: `
          radial-gradient(ellipse at 0% 0%, ${accent}22 0%, transparent 55%),
          linear-gradient(135deg, #1a1d27 0%, #252a38 55%, #1e2230 100%)
        `,
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: -40,
          top: -40,
          width: 160,
          height: 160,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${accent}33 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(145deg, ${accent} 0%, ${accent}cc 100%)`,
              color: "#fff",
              boxShadow: `0 8px 18px ${accent}55`,
            }}
          >
            {Icon ? <Icon sx={{ fontSize: 24 }} /> : null}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <StackRow>
              <Chip
                size="small"
                label={eyebrow}
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  bgcolor: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.18)",
                }}
              />
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Step {step} of {totalSteps}
              </Typography>
            </StackRow>
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 800,
                fontSize: { xs: 22, md: 26 },
                letterSpacing: "-0.02em",
                lineHeight: 1.15,
                mt: 0.7,
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.68)",
                  fontSize: 13.5,
                  mt: 0.55,
                  maxWidth: 520,
                  lineHeight: 1.45,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 0.7,
            flexShrink: 0,
          }}
        >
          {Array.from({ length: totalSteps }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i + 1 === step ? 28 : 10,
                height: 10,
                borderRadius: 99,
                bgcolor:
                  i + 1 === step ? accent : "rgba(255,255,255,0.22)",
                transition: "width 0.25s ease, background 0.25s ease",
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>

    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        background:
          "linear-gradient(180deg, #f7f8fb 0%, #f3f4f8 100%)",
      }}
    >
      {children}
    </Box>
  </Paper>
);

const StackRow = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
    {children}
  </Box>
);

const Section = ({ title, subtitle, children, accent = "#ef364e" }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, md: 2.5 },
      mb: 2,
      borderRadius: 2.5,
      border: "1px solid #e5e7eb",
      background: "#fff",
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
      "&:last-child": { mb: 0 },
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        borderColor: `${accent}55`,
        boxShadow: `0 6px 18px ${accent}10`,
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: subtitle ? 0.4 : 1.5,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: accent,
          boxShadow: `0 0 0 3px ${accent}22`,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontWeight: 700, fontSize: 15.5, color: "#111827" }}>
        {title}
      </Typography>
    </Box>
    {subtitle ? (
      <Typography sx={{ color: "#6b7280", fontSize: 13, mb: 1.75, pl: 2.25 }}>
        {subtitle}
      </Typography>
    ) : null}
    {children}
  </Paper>
);

const CompetitionForm = ({
  formik,
  edited,
  votingLocked,
  competitionStatus = "scheduled",
  competitionId,
}) => {
  const events = formik.values.events || [];
  const categories = formik.values.categories || [];
  const isStopped = edited && competitionStatus === "stopped";
  const scheduleLocked = isStopped;
  const structureLocked = isStopped;

  const addCategory = () => {
    if (structureLocked || categories.length >= MAX_CATEGORIES) return;
    formik.setFieldValue("categories", [...categories, ""]);
  };

  const removeCategory = (index) => {
    if (structureLocked) return;
    formik.setFieldValue(
      "categories",
      categories.filter((_, i) => i !== index)
    );
  };

  const updateCategory = (index, value) => {
    if (structureLocked) return;
    const next = categories.map((c, i) => (i === index ? value : c));
    formik.setFieldValue("categories", next);
  };

  const addEvent = () => {
    if (structureLocked || events.length >= MAX_EVENTS) return;
    formik.setFieldValue("events", [...events, { name: "" }]);
  };

  const removeEvent = (index) => {
    if (structureLocked) return;
    formik.setFieldValue(
      "events",
      events.filter((_, i) => i !== index)
    );
  };

  const updateEvent = (index, patch) => {
    if (structureLocked) return;
    const next = events.map((e, i) => (i === index ? { ...e, ...patch } : e));
    formik.setFieldValue("events", next);
  };

  const startMin = (() => {
    if (!edited || scheduleLocked) return localNowMin();
    const current = formik.values.start_date;
    if (current && dayjs(current).isBefore(dayjs())) return current;
    return localNowMin();
  })();

  return (
    <Box className="page_inner" sx={{ maxWidth: 920, pb: 4 }}>
      <form onSubmit={formik.handleSubmit}>
        {isStopped ? (
          <Alert severity="warning" sx={{ mb: 2.5 }}>
            This competition is permanently stopped and cannot be resumed.
            You can only update name, description, and media. Schedule,
            categories, and events are locked.
          </Alert>
        ) : null}
        <StageBlock
          step={1}
          eyebrow="STEP 1 · SETUP"
          title="Competition setup"
          subtitle={`Foundation for ${BEAUTY_PAGEANT_LABEL} — details, media, schedule, categories, events, and pre-round voting rules.`}
          accent="#ef364e"
          icon={StadiumOutlinedIcon}
        >
          <Section
            title="Basic details"
            subtitle="Name and description for this competition."
            accent="#ef364e"
          >
            <Typography className="label" sx={{ mb: 0.5 }}>
              Competition Name
            </Typography>
            <Field
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter competition name"
            />
            {formik.touched.name && formik.errors.name ? (
              <FormHelperText sx={{ color: "red" }}>
                {formik.errors.name}
              </FormHelperText>
            ) : null}

            <Typography className="label" sx={{ mt: 2, mb: 0.5 }}>
              Description
            </Typography>
            <Field
              name="description"
              multiline
              rows={3}
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter description"
            />
            {formik.touched.description && formik.errors.description ? (
              <FormHelperText sx={{ color: "red" }}>
                {formik.errors.description}
              </FormHelperText>
            ) : null}
          </Section>

          <Section
            title="Media"
            subtitle="Banner image and promotional video."
            accent="#ef364e"
          >
            <Typography className="label" sx={{ mb: 0.5 }}>
              Banner Image
            </Typography>
            <Box className="Banner_img_preview banner-preview-wide" sx={{ mb: 2 }}>
              <BannerImage
                formik={formik}
                edited={edited}
                autoFit={{
                  width: ADVERTISEMENT_BANNER_WIDTH,
                  height: ADVERTISEMENT_BANNER_HEIGHT,
                }}
              />
              {formik.touched.image && formik.errors.image ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.image}
                </FormHelperText>
              ) : null}
            </Box>
            <Typography className="label" sx={{ mb: 0.5 }}>
              Promotional Video
            </Typography>
            <PromoVideoUpload formik={formik} edited={edited} />
          </Section>

          <Section
            title="Schedule"
            subtitle="Competition becomes Active automatically on the start date."
            accent="#ef364e"
          >
            {scheduleLocked ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                {isStopped
                  ? "Start and end dates are locked because this competition is permanently stopped."
                  : "Start and end dates are locked while the competition is Active."}
              </Alert>
            ) : null}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography className="label" sx={{ mb: 0.5 }}>
                  Start Date
                </Typography>
                <Field
                  type="datetime-local"
                  name="start_date"
                  value={formik.values.start_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={scheduleLocked}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startMin }}
                  InputProps={{
                    className: "datetime-field",
                    onClick: scheduleLocked ? undefined : openDatePicker,
                  }}
                />
                {formik.touched.start_date && formik.errors.start_date ? (
                  <FormHelperText sx={{ color: "red" }}>
                    {formik.errors.start_date}
                  </FormHelperText>
                ) : null}
              </Box>
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <Typography className="label" sx={{ mb: 0.5 }}>
                  End Date
                </Typography>
                <Field
                  type="datetime-local"
                  name="end_date"
                  value={formik.values.end_date}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={scheduleLocked}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: formik.values.start_date || localNowMin(),
                  }}
                  InputProps={{
                    className: "datetime-field",
                    onClick: scheduleLocked ? undefined : openDatePicker,
                  }}
                />
                {formik.touched.end_date && formik.errors.end_date ? (
                  <FormHelperText sx={{ color: "red" }}>
                    {formik.errors.end_date}
                  </FormHelperText>
                ) : null}
              </Box>
            </Box>
          </Section>

          <Section
            title="Categories"
            subtitle="Add custom categories for this competition (e.g. Adult Women). Up to 10."
            accent="#ef364e"
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography sx={{ color: "#6b7280", fontSize: 13 }}>
                {categories.length} / {MAX_CATEGORIES} categories
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addCategory}
                disabled={
                  structureLocked || categories.length >= MAX_CATEGORIES
                }
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#ef364e",
                  color: "#ef364e",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Add Category
              </Button>
            </Box>

            {formik.errors.categories &&
            typeof formik.errors.categories === "string" ? (
              <FormHelperText sx={{ color: "red", mb: 1 }}>
                {formik.errors.categories}
              </FormHelperText>
            ) : null}

            {categories.length === 0 ? (
              <Typography sx={{ color: "#9ca3af", fontSize: 14, mb: 1 }}>
                No categories yet. Add at least one.
              </Typography>
            ) : (
              categories.map((cat, index) => (
                <Box key={index} sx={{ mb: 1.2 }}>
                  <Field
                    value={cat}
                    disabled={structureLocked}
                    onChange={(e) => updateCategory(index, e.target.value)}
                    placeholder="e.g. Adult Women"
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          size="small"
                          aria-label="Remove category"
                          onClick={() => removeCategory(index)}
                          disabled={structureLocked || categories.length <= 1}
                          edge="end"
                          sx={{
                            color: "#9ca3af",
                            "&:hover": { color: "#ef364e" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ),
                    }}
                  />
                  {formik.errors.categories?.[index] ? (
                    <FormHelperText sx={{ color: "red" }}>
                      {formik.errors.categories[index]}
                    </FormHelperText>
                  ) : null}
                </Box>
              ))
            )}
          </Section>

          <Section
            title="Competition events"
            subtitle="Events contestants upload videos or images for (e.g. Advocacy, National Costume). Up to 10."
            accent="#ef364e"
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1.5,
              }}
            >
              <Typography sx={{ color: "#6b7280", fontSize: 13 }}>
                {events.length} / {MAX_EVENTS} events
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={addEvent}
                disabled={structureLocked || events.length >= MAX_EVENTS}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#ef364e",
                  color: "#ef364e",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Add Event
              </Button>
            </Box>

            {formik.errors.events && typeof formik.errors.events === "string" ? (
              <FormHelperText sx={{ color: "red", mb: 1 }}>
                {formik.errors.events}
              </FormHelperText>
            ) : null}

            {events.map((event, index) => (
              <Box
                key={index}
                sx={{
                  border: "1px solid #eceef3",
                  borderRadius: 2,
                  p: 2,
                  mb: 1.5,
                  background: "#fafbfc",
                }}
              >
                <Typography className="label" sx={{ mb: 0.5 }}>
                  Event {index + 1} name
                </Typography>
                <Field
                  value={event.name}
                  disabled={structureLocked}
                  onChange={(e) =>
                    updateEvent(index, { name: e.target.value })
                  }
                  placeholder="e.g. Advocacy"
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        aria-label="Remove event"
                        onClick={() => removeEvent(index)}
                        disabled={structureLocked || events.length <= 1}
                        edge="end"
                        sx={{
                          color: "#9ca3af",
                          "&:hover": { color: "#ef364e" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
                {formik.errors.events?.[index]?.name ? (
                  <FormHelperText sx={{ color: "red" }}>
                    {formik.errors.events[index].name}
                  </FormHelperText>
                ) : null}
              </Box>
            ))}

            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: "1px solid #eceef3",
                background: "#fff",
              }}
            >
              <Typography className="label" sx={{ mb: 0.5 }}>
                Free votes per day
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#4b5563", mb: 1.25 }}>
                How many free votes each user can cast per calendar day in this
                competition (across all events). After that, they must purchase
                votes. Default is 1.
              </Typography>
              <Field
                type="number"
                name="max_free_votes_per_day"
                value={formik.values.max_free_votes_per_day ?? 1}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                inputProps={{ min: 1, step: 1 }}
                sx={{ maxWidth: 200 }}
              />
              {formik.touched.max_free_votes_per_day &&
              formik.errors.max_free_votes_per_day ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.max_free_votes_per_day}
                </FormHelperText>
              ) : null}
            </Box>

            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                border: "1px solid #eceef3",
                background: "#fff",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                <PaymentsOutlinedIcon sx={{ fontSize: 20, color: "#C9A66B" }} />
                <Typography className="label">Vote purchase</Typography>
              </Stack>
              <Typography sx={{ fontSize: 13, color: "#4b5563", mb: 1.5 }}>
                Set per-competition vote packs — number of votes and price for each
                pack. Each competition can offer different pack sizes and prices.
                Turn payments on or off at any time — when off, voters see that
                payments are suspended.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formik.values.votes_purchase_enabled)}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "votes_purchase_enabled",
                        e.target.checked
                      )
                    }
                    color="error"
                  />
                }
                label={
                  formik.values.votes_purchase_enabled
                    ? "Vote payments enabled"
                    : "Vote payments suspended"
                }
                sx={{ mb: 2, display: "flex", ml: 0 }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={2}>
                <Field
                  select
                  name="vote_purchase_currency"
                  label="Currency"
                  value={formik.values.vote_purchase_currency || "GBP"}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ maxWidth: 160 }}
                >
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="USD">USD ($)</MenuItem>
                </Field>
              </Stack>

              <Stack spacing={2}>
                {[
                  {
                    label: "Pack 1",
                    votesField: "vote_pack_5_votes",
                    priceField: "vote_pack_5_price",
                  },
                  {
                    label: "Pack 2",
                    votesField: "vote_pack_10_votes",
                    priceField: "vote_pack_10_price",
                  },
                  {
                    label: "Pack 3",
                    votesField: "vote_pack_50_votes",
                    priceField: "vote_pack_50_price",
                  },
                ].map(({ label, votesField, priceField }) => (
                  <Box
                    key={votesField}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid #eceef3",
                      bgcolor: "#fff",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                      {label}
                    </Typography>
                    <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                          Number of votes
                        </Typography>
                        <Field
                          type="number"
                          name={votesField}
                          value={formik.values[votesField] ?? ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          inputProps={{ min: 1, step: 1 }}
                          sx={{ maxWidth: 160 }}
                        />
                        {formik.touched[votesField] && formik.errors[votesField] ? (
                          <FormHelperText sx={{ color: "red" }}>
                            {formik.errors[votesField]}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                          Price
                        </Typography>
                        <Field
                          type="number"
                          name={priceField}
                          value={formik.values[priceField] ?? ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ maxWidth: 160 }}
                        />
                        {formik.touched[priceField] && formik.errors[priceField] ? (
                          <FormHelperText sx={{ color: "red" }}>
                            {formik.errors[priceField]}
                          </FormHelperText>
                        ) : null}
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Section>

          <Alert
            severity="info"
            icon={<OpenInNewIcon fontSize="inherit" />}
            sx={{ mt: 0.5 }}
          >
            <Typography sx={{ fontSize: 13, mb: competitionId ? 1 : 0 }}>
              Final Night scoring weights, rounds, and judges are configured on
              the separate <strong>Final Night Control</strong> page — not here
              during pre-round setup.
              {edited
                ? " Open it from the competitions list after the competition is stopped."
                : " After you create this competition, use Final Night Control when you are ready for the live show."}
            </Typography>
            {competitionId ? (
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                sx={{
                  borderColor: "#C9A66B",
                  color: "#9a7b3f",
                  textTransform: "none",
                  fontWeight: 700,
                }}
                onClick={() => {
                  window.location.href = `/admin/dashboard/competitions/final-night/${competitionId}`;
                }}
              >
                Open Final Night Control
              </Button>
            ) : null}
          </Alert>
        </StageBlock>

        <Divider sx={{ my: 2 }} />
        <Button
          className="pt-create"
          type="submit"
          variant="contained"
          sx={{
            background: "#ef364e",
            px: 4,
            py: 1.2,
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            fontSize: 15,
            "&:hover": { background: "#d32d43" },
          }}
        >
          {edited
            ? isStopped
              ? "Update display details"
              : "Update Competition"
            : "Create Competition"}
        </Button>
      </form>
    </Box>
  );
};

export default CompetitionForm;
